import { useEffect, useState, useCallback, useMemo } from "react";
import {
  fetchAdminSeo,
  adminCreateSeo,
  adminUpdateSeo,
  adminDeleteSeo,
} from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  Plus,
  Trash2,
  Search,
  ExternalLink,
  Save,
  Globe,
  Box,
  Link2,
  Copy,
  Check,
} from "lucide-react";

/**
 * SEO / XML Sitemap admin tab.
 *
 * Source: Airtable `SEO-Site Map` (fields: url, slug, Page_type, title,
 * h1, meta_description, canonical_url, og_title/_description/_image,
 * changefreq, priority, status, Category, Products).
 *
 * Rows can be linked to a product (`Products` field) OR be independent
 * (Home, Category, ContactUs, etc.).  Filter switches between the two
 * sets to keep the table workable.
 */
export default function SeoTab({ callerUserRecordId }) {
  const [rows, setRows] = useState([]);
  const [pageTypeCounts, setPageTypeCounts] = useState({});
  const [productLinked, setProductLinked] = useState(0);
  const [independent, setIndependent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("all"); // 'all' | 'product' | 'independent'
  const [pageType, setPageType] = useState("");
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [copied, setCopied] = useState(false);
  const [newRow, setNewRow] = useState({
    url: "",
    slug: "",
    Page_type: "Home",
    title: "",
    meta_description: "",
    status: "published",
  });

  const sitemapUrl = "https://mindfulminerals.com/sitemap.xml";

  const load = useCallback(async () => {
    if (!callerUserRecordId) return;
    setLoading(true);
    try {
      const res = await fetchAdminSeo({ callerUserRecordId });
      setRows(res?.rows || []);
      setPageTypeCounts(res?.page_type_counts || {});
      setProductLinked(res?.product_linked || 0);
      setIndependent(res?.independent || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  }, [callerUserRecordId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (scope === "product" && !r.is_product_linked) return false;
      if (scope === "independent" && r.is_product_linked) return false;
      if (pageType && r.page_type !== pageType) return false;
      if (
        ql &&
        !(
          r.url.toLowerCase().includes(ql) ||
          r.title.toLowerCase().includes(ql) ||
          r.slug.toLowerCase().includes(ql)
        )
      )
        return false;
      return true;
    });
  }, [rows, scope, pageType, q]);

  function patch(id, k, v) {
    setEdits((cur) => ({ ...cur, [id]: { ...(cur[id] || {}), [k]: v } }));
  }

  async function save(id) {
    const fields = edits[id];
    if (!fields || Object.keys(fields).length === 0) {
      setEditingId(null);
      return;
    }
    setSavingId(id);
    try {
      const res = await adminUpdateSeo({
        callerUserRecordId,
        recordId: id,
        fields,
      });
      setRows((cur) => cur.map((r) => (r.id === id ? res.row : r)));
      setEdits((cur) => {
        const { [id]: _drop, ...rest } = cur;
        return rest;
      });
      setEditingId(null);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingId(null);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this SEO entry?")) return;
    try {
      await adminDeleteSeo({ callerUserRecordId, recordId: id });
      setRows((cur) => cur.filter((r) => r.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }

  async function addNew() {
    if (!newRow.url.trim()) {
      toast.error("url is required");
      return;
    }
    setAdding(true);
    try {
      const res = await adminCreateSeo({
        callerUserRecordId,
        fields: {
          url: newRow.url.trim(),
          slug: newRow.slug.trim(),
          Page_type: newRow.Page_type,
          title: newRow.title.trim(),
          meta_description: newRow.meta_description.trim(),
          status: newRow.status,
        },
      });
      setRows((cur) => [res.row, ...cur]);
      setNewRow({
        url: "",
        slug: "",
        Page_type: "Home",
        title: "",
        meta_description: "",
        status: "published",
      });
      toast.success("Entry added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setAdding(false);
    }
  }

  async function copySitemap() {
    try {
      await navigator.clipboard.writeText(sitemapUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  }

  const tiles = [
    {
      label: "Total Entries",
      value: rows.length,
      icon: Globe,
      tint: "bg-violet-50 text-violet-700",
    },
    {
      label: "Product-Linked",
      value: productLinked,
      icon: Box,
      tint: "bg-emerald-50 text-emerald-700",
    },
    {
      label: "Independent",
      value: independent,
      icon: Link2,
      tint: "bg-amber-50 text-amber-700",
    },
  ];

  const pageTypes = Object.keys(pageTypeCounts).sort();

  return (
    <div data-testid="seo-tab">
      {/* Counts + tools */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-3">
        {tiles.map((t) => {
          const I = t.icon;
          return (
            <div
              key={t.label}
              className="rounded-2xl border border-zinc-200 bg-white p-3"
            >
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-zinc-500 font-semibold">
                <span
                  className={`w-6 h-6 rounded-md inline-flex items-center justify-center ${t.tint}`}
                >
                  <I size={12} />
                </span>
                {t.label}
              </div>
              <div className="mt-1.5 text-2xl font-bold text-zinc-900">
                {t.value}
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="text-[11px] uppercase tracking-wide font-bold text-zinc-500 mr-2">
          XML Sitemap
        </div>
        <code className="text-xs bg-zinc-100 px-2 py-1 rounded break-all">
          {sitemapUrl}
        </code>
        <button
          onClick={copySitemap}
          className="h-8 px-2 rounded-md bg-white border border-zinc-200 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-zinc-50"
          data-testid="seo-copy-sitemap"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? "Copied" : "Copy"}
        </button>
        <a
          href="https://search.google.com/search-console"
          target="_blank"
          rel="noopener noreferrer"
          className="h-8 px-2 rounded-md bg-zinc-900 text-white text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-zinc-800"
        >
          <ExternalLink size={12} /> Google Search Console
        </a>
        <a
          href="https://www.bing.com/webmasters"
          target="_blank"
          rel="noopener noreferrer"
          className="h-8 px-2 rounded-md bg-white border border-zinc-200 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-zinc-50"
        >
          <ExternalLink size={12} /> Bing Webmaster
        </a>
      </div>

      {/* Add new */}
      <div className="rounded-2xl border border-zinc-200 bg-white p-3 mb-4">
        <div className="text-[11px] font-bold uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-1.5">
          <Plus size={12} /> Add SEO Entry
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
          <Input
            placeholder="https://mindfulminerals.com/..."
            value={newRow.url}
            onChange={(e) =>
              setNewRow((s) => ({ ...s, url: e.target.value }))
            }
            className="h-9 col-span-2"
            data-testid="seo-new-url"
          />
          <Input
            placeholder="slug"
            value={newRow.slug}
            onChange={(e) =>
              setNewRow((s) => ({ ...s, slug: e.target.value }))
            }
            className="h-9"
          />
          <Input
            placeholder="Page type (Home / Poduct / Category)"
            value={newRow.Page_type}
            onChange={(e) =>
              setNewRow((s) => ({ ...s, Page_type: e.target.value }))
            }
            className="h-9"
          />
          <Input
            placeholder="title"
            value={newRow.title}
            onChange={(e) =>
              setNewRow((s) => ({ ...s, title: e.target.value }))
            }
            className="h-9"
          />
          <button
            onClick={addNew}
            disabled={adding}
            className="h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5"
            data-testid="seo-add"
          >
            {adding ? (
              <Loader2 className="animate-spin" size={13} />
            ) : (
              <>
                <Plus size={13} /> Add
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="inline-flex rounded-xl bg-white border border-zinc-200 p-0.5">
          {[
            { k: "all", l: "All" },
            { k: "product", l: "Product-Linked" },
            { k: "independent", l: "Independent" },
          ].map((s) => (
            <button
              key={s.k}
              onClick={() => setScope(s.k)}
              className={`h-7 px-3 rounded-lg text-xs font-semibold ${
                scope === s.k
                  ? "bg-zinc-900 text-white"
                  : "text-zinc-700 hover:bg-zinc-50"
              }`}
              data-testid={`seo-scope-${s.k}`}
            >
              {s.l}
            </button>
          ))}
        </div>
        <select
          value={pageType}
          onChange={(e) => setPageType(e.target.value)}
          className="h-8 rounded-md border border-zinc-200 px-2 text-sm bg-white"
          data-testid="seo-page-type"
        >
          <option value="">All page types</option>
          {pageTypes.map((p) => (
            <option key={p} value={p}>
              {p} ({pageTypeCounts[p]})
            </option>
          ))}
        </select>
        <div className="relative ml-auto flex-1 min-w-[180px] max-w-[320px]">
          <Search
            size={13}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400"
          />
          <Input
            placeholder="Search URL, slug, title…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-7 h-8 text-sm"
            data-testid="seo-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center">
          <Loader2 className="animate-spin text-zinc-400" size={20} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-zinc-500">
          <Globe className="mx-auto mb-2 text-zinc-300" size={28} />
          No entries match.
        </div>
      ) : (
        <div className="rounded-2xl border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[11px] uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">URL</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Title</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.map((r) => {
                const e = edits[r.id] || {};
                const isEdit = editingId === r.id;
                const v = (k, fallback) =>
                  e[k] !== undefined ? e[k] : fallback;
                return (
                  <tr key={r.id} data-testid={`seo-row-${r.id}`}>
                    <td className="px-3 py-2 max-w-[320px]">
                      {isEdit ? (
                        <Input
                          value={v("url", r.url)}
                          onChange={(ev) => patch(r.id, "url", ev.target.value)}
                          className="h-8 text-sm"
                        />
                      ) : (
                        <a
                          href={r.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-violet-700 hover:underline truncate inline-flex items-center gap-1 max-w-full"
                          title={r.url}
                        >
                          <span className="truncate">{r.url}</span>
                          <ExternalLink
                            size={11}
                            className="shrink-0 text-zinc-400"
                          />
                        </a>
                      )}
                      <div className="text-[10px] text-zinc-400 mt-0.5 flex items-center gap-1">
                        {r.is_product_linked ? (
                          <>
                            <Box size={10} /> Product-linked
                          </>
                        ) : (
                          <>
                            <Link2 size={10} /> Independent
                          </>
                        )}
                        {r.slug && <span>· /{r.slug}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {isEdit ? (
                        <Input
                          value={v("Page_type", r.page_type)}
                          onChange={(ev) =>
                            patch(r.id, "Page_type", ev.target.value)
                          }
                          className="h-8 text-sm w-28"
                        />
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded bg-zinc-100 text-zinc-700 text-[11px] font-semibold">
                          {r.page_type || "—"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 max-w-[260px]">
                      {isEdit ? (
                        <Input
                          value={v("title", r.title)}
                          onChange={(ev) =>
                            patch(r.id, "title", ev.target.value)
                          }
                          className="h-8 text-sm"
                        />
                      ) : (
                        <span className="text-zinc-700 truncate inline-block max-w-full" title={r.title}>
                          {r.title || "—"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isEdit ? (
                        <Input
                          value={v("status", r.status)}
                          onChange={(ev) =>
                            patch(r.id, "status", ev.target.value)
                          }
                          className="h-8 text-sm w-24"
                        />
                      ) : (
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-[11px] font-semibold ${
                            r.status === "published"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-zinc-100 text-zinc-600"
                          }`}
                        >
                          {r.status || "—"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {isEdit ? (
                        <button
                          onClick={() => save(r.id)}
                          disabled={savingId === r.id}
                          className="mr-1 inline-flex items-center justify-center w-7 h-7 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                          data-testid={`seo-save-${r.id}`}
                        >
                          {savingId === r.id ? (
                            <Loader2 className="animate-spin" size={12} />
                          ) : (
                            <Save size={12} />
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => setEditingId(r.id)}
                          className="mr-1 inline-flex items-center justify-center w-7 h-7 rounded-md text-zinc-500 hover:bg-zinc-100"
                          data-testid={`seo-edit-${r.id}`}
                        >
                          ✎
                        </button>
                      )}
                      <button
                        onClick={() => remove(r.id)}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-rose-500 hover:bg-rose-50"
                        data-testid={`seo-delete-${r.id}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
